import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ApplicationForm, ApplicationFormData } from './ApplicationForm';

interface RequestPageProps {
  onLogout: () => void;
}

const CREATE_APPLICATION_URL = `${process.env.REACT_APP_API_BASE_URL}/api/v1/applications/full`;
const DRAFTS_URL = `${process.env.REACT_APP_API_BASE_URL}/api/v1/application-drafts/`;

export const formatPayload = (formData: ApplicationFormData) => {
  const organizationName = formData.organization?.fullName || formData.organization?.name || '';
  return {
    organization_id: formData.organization?.id ? Number(formData.organization.id) : null,
    responsible_specialist_id: formData.specialist?.id ? Number(formData.specialist.id) : null,
    subfolder_name: formData.subfolderName.trim() || null,
    exam_export_folder: formData.subfolderName.trim() || null,
    students: formData.students.map(student => ({
      full_name: student.full_name,
      email: student.email,
      organization: organizationName,
      programs: student.programs.map(pa => ({
        program_id: Number(pa.program.id),
        program_name: pa.program.name,
        theoretical_start_date: pa.theoretical_start_date || null,
        theoretical_end_date: pa.theoretical_end_date || null,
        exam_date: pa.exam_date || null,
      })),
    })),
    ui_state: formData
  };
};

const RequestPage: React.FC<RequestPageProps> = ({ onLogout }) => {
  const navigate = useNavigate();
  const [draftId, setDraftId] = useState<number | null>(null);

  const draftIdRef = useRef<number | null>(null);

  const userStr = localStorage.getItem('user');
  const user = userStr ? JSON.parse(userStr) : {};

  const username = user?.full_name || user?.username || '—';

  let roleName = 'user';
  if (user?.role) {
    if (typeof user.role === 'string') {
        roleName = user.role;
    } else if (typeof user.role === 'object' && user.role.name) {
        roleName = user.role.name;
    }
  }

  const normalizedRole = roleName.toLowerCase();

  useEffect(() => {
    if (normalizedRole !== 'admin') {
      navigate('/applications', { replace: true });
    }
  }, [normalizedRole, navigate]);

  useEffect(() => {
    draftIdRef.current = draftId;
  }, [draftId]);

  const handleAutoSaveDraft = useCallback((formData: ApplicationFormData) => {
    const token = localStorage.getItem('token');
    const payload = { data: formatPayload(formData), last_step: "form" };

    const currentDraftId = draftIdRef.current;
    const url = currentDraftId ? `${DRAFTS_URL}${currentDraftId}` : DRAFTS_URL;
    const method = currentDraftId ? "PATCH" : "POST";

    fetch(url, {
      method,
      headers: {
        "Content-Type": "application/json",
        "Authorization": token || ''
      },
      body: JSON.stringify(payload),
      keepalive: true
    }).catch(console.error);
  }, []);

  if (normalizedRole !== 'admin') {
    return null;
  }

  const handleCreateApplication = async (formData: ApplicationFormData) => {
    const token = localStorage.getItem('token');

    if (draftId) {
      const patchRes = await fetch(`${DRAFTS_URL}${draftId}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "Authorization": token || ''
        },
        body: JSON.stringify({ data: formatPayload(formData), last_step: "ready" })
      });
      if (!patchRes.ok) throw new Error("Ошибка обновления черновика перед публикацией");

      const publishRes = await fetch(`${DRAFTS_URL}${draftId}/publish`, {
        method: "POST",
        headers: { "Authorization": token || '' }
      });
      if (!publishRes.ok) {
        let msg = "Ошибка публикации";
        try { const err = await publishRes.json(); msg = err?.detail || msg; } catch(e){}
        throw new Error(msg);
      }

      const data = await publishRes.json();
      navigate('/applications');
      return data;
    }

    const { ui_state, ...payload } = formatPayload(formData);

    const res = await fetch(CREATE_APPLICATION_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Accept": "application/json",
        "Authorization": token || ''
      },
      body: JSON.stringify(payload),
    });

    if (res.status === 401) {
        onLogout();
        throw new Error("Сессия истекла. Пожалуйста, войдите снова.");
    }

    if (!res.ok) {
      let errorMessage = `Ошибка сервера (${res.status})`;
      try {
        const errorBody = await res.json();
        errorMessage = errorBody?.detail || JSON.stringify(errorBody) || errorMessage;
      } catch (e) {
        // Ignore if response is not JSON
      }
      throw new Error(errorMessage);
    }

    const data = await res.json();
    navigate('/applications');
    return data;
  };

  const handleSaveDraft = async (formData: ApplicationFormData) => {
    const token = localStorage.getItem('token');
    const payload = { data: formatPayload(formData), last_step: "form" };

    const url = draftId ? `${DRAFTS_URL}${draftId}` : DRAFTS_URL;
    const method = draftId ? "PATCH" : "POST";

    const res = await fetch(url, {
      method,
      headers: {
        "Content-Type": "application/json",
        "Authorization": token || ''
      },
      body: JSON.stringify(payload)
    });

    if (res.status === 401) {
      onLogout();
      throw new Error("Сессия истекла");
    }

    if (!res.ok) {
      throw new Error("Ошибка сохранения черновика");
    }

    const data = await res.json();
    if (!draftId) {
      setDraftId(data.id);
    }
    navigate('/drafts');
    return data;
  };

  return (
    <ApplicationForm
      onLogout={onLogout}
      isEditing={false}
      onSubmit={handleCreateApplication}
      onSaveDraft={handleSaveDraft}
      onAutoSaveDraft={handleAutoSaveDraft}
      username={username}
      role={roleName}
    />
  );
};

export default RequestPage;